import React, { useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import HeadShot from "../profilePhoto/HeadShot";
import ProfileCommonFunction from "../ProfileCard/ProfileCommonFunction";
import TrophyIcon from "../../images/trophy.png";
import { coachProfileService } from "../../services";
import RouteConstants from "../../common/RouteConstants";
import AppConstants from "../../common/AppConstants";
import { AppDataContext } from "../../common/AppContext";
import { useCoachProfileDialog } from "../../screens/CoachesManagement/CoachProfileDialogContext";
import useTelemetry from "../../utils/useTelemetry";

const SimilarProfileItem = ({ item, getLeagueTextByValue, onClick }) => {
  const fullName =
    (item.personalDetails && item.personalDetails.fullName) || {};
  const name = `${fullName.firstName || ""} ${fullName.lastName || ""}`.trim();
  const itemUserName = item.userName || "";
  const profilePhoto =
    (item.personalDetails && item.personalDetails.profilePhoto) || "";

  const { currentExp } = ProfileCommonFunction.getCurrentCoachingExperience(
    item.coachingAndOtherJobExperience,
  );
  const yearOfExp = ProfileCommonFunction.getYearOfExperience(
    item.coachingAndOtherJobExperience,
  );
  const wonChampionship = ProfileCommonFunction.getWonChampionship(
    item.coachingAndOtherJobExperience,
    item.playingExperience,
  );

  const primaryExp = currentExp && currentExp.length > 0 ? currentExp[0] : null;
  const extraExpCount =
    currentExp && currentExp.length > 1 ? currentExp.length - 1 : 0;
  const extraTooltip =
    extraExpCount > 0
      ? currentExp
          .slice(1)
          .map(
            (e) =>
              `${e.title} - ${e.teamName}, ${getLeagueTextByValue ? getLeagueTextByValue(e.league) : e.league}`,
          )
          .join("\n")
      : "";

  return (
    <Box
      onClick={() => onClick(itemUserName)}
      sx={{
        py: 1.25,
        cursor: itemUserName ? "pointer" : "default",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            position: "relative",
            flexShrink: 0,
            width: 56,
            height: 56,
            "& .MuiAvatar-root": {
              width: 56,
              height: 56,
              fontSize: 20,
              border: (theme) =>
                `2px solid ${theme.palette.mode === "dark" ? "#7B99D7" : theme.palette.primary.main}`,
            },
          }}
        >
          <HeadShot
            userName={itemUserName}
            coachName={name}
            profilePhoto={profilePhoto}
          />
          {wonChampionship > 0 && (
            <Tooltip title={`${wonChampionship} championships won`}>
              <Box
                component="img"
                src={TrophyIcon}
                alt="championships won"
                sx={{
                  position: "absolute",
                  width: 22,
                  height: 22,
                  right: -4,
                  bottom: -4,
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  border: "1.5px solid #fff",
                  boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                  pointerEvents: "auto",
                }}
              />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, color: (theme) => theme.palette.mode === "dark" ? "#ffffff" : undefined }}>
          <Typography
            sx={{ fontWeight: "bolder", fontSize: 16, lineHeight: 1.2, color: (theme) => theme.palette.mode === "dark" ? "#ffffff" : undefined }}
            noWrap
          >
            {name || itemUserName}
          </Typography>
          {primaryExp ? (
            <>
              <Typography variant="caption" display="block" noWrap>
                {primaryExp.title}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, minWidth: 0 }}
                  noWrap
                >
                  {primaryExp.teamName}
                  {primaryExp.league
                    ? `, ${getLeagueTextByValue ? getLeagueTextByValue(primaryExp.league) : primaryExp.league}`
                    : ""}
                </Typography>
                {extraExpCount > 0 && (
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: "pre-line" }}>
                        {extraTooltip}
                      </span>
                    }
                  >
                    <Box
                      sx={{
                        height: 22,
                        minWidth: 22,
                        px: 0.75,
                        backgroundColor: "#7B99D7",
                        borderRadius: "50%",
                        color: "#fff",
                        textAlign: "center",
                        lineHeight: "22px",
                        fontWeight: "bold",
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      +{extraExpCount}
                    </Box>
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.75)" : "text.secondary" }}>
                {yearOfExp.year} years & {yearOfExp.month} months
              </Typography>
            </>
          ) : (
            <Typography variant="caption" display="block">
              {AppConstants.CURRENTLY_NOT_WORKING}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

const SimilarProfilesList = ({ userName }) => {
  const navigate = useNavigate();
  const dialogCtx = useCoachProfileDialog();
  const { getLeagueTextByValue } = useContext(AppDataContext);
  const { trackCustomEvents, trackException } = useTelemetry();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userName) {
      setRecommendations([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    coachProfileService
      .getCoachRecommendations(userName)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res)
          ? res
          : res && Array.isArray(res.recommendations)
            ? res.recommendations
            : res && Array.isArray(res.data)
              ? res.data
              : [];
        setRecommendations(list || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setRecommendations([]);
          trackException(err, { component: 'SimilarProfilesList', action: 'getCoachRecommendations' }, 'exc');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userName]);

  const handleClick = (itemUserName) => {
    if (!itemUserName) return;
    // trackCustomEvents("ClickEvent", {
    //   event: "Similar Profile Visit",
    //   profileName: itemUserName,
    // });
    trackCustomEvents(
      "Similar Profile Visit",
      {
        profileName: itemUserName,
      },
      "event",
    );
    const path = RouteConstants.COACHES_PROFILE_VIEW + "/" + itemUserName;
    if (dialogCtx && dialogCtx.dialogNavigate) {
      dialogCtx.dialogNavigate(path);
    } else {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <Typography variant="caption" sx={{ px: 1 }}>
        Loading...
      </Typography>
    );
  }
  if (!recommendations.length) {
    return (
      <Typography variant="caption" sx={{ px: 1 }}>
        No similar profiles found.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {recommendations.map((item, idx) => (
        <React.Fragment key={item.userName || idx}>
          <SimilarProfileItem
            item={item}
            getLeagueTextByValue={getLeagueTextByValue}
            onClick={handleClick}
          />
          {idx < recommendations.length - 1 && (
            <Divider sx={{ borderColor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : undefined }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default SimilarProfilesList;
